import React, { useEffect, useRef, useState } from "react";
import {
  ChevronDown,
  Save,
  Columns3,
  Loader2,
  X,
  Check,
  Trash2,
  Star,
} from "lucide-react";
import TableColumnManagerModal from "./TableColumnManagerModal";
import ConfirmDeleteModal from "../../../components/ACS-Dashboard/ACS-Blocks/ConfirmDeleteModal";

const TableSavedViewsControl = ({
  label = "Views",
  controlLabel = "Default",
  views = [],
  activeView,
  loading = false,
  error = null,
  isDirty = false,
  allDataHeaders = [],
  dataHeaders = [],
  onSelectView,
  onSaveNew,
  onUpdateActive,
  onRename,
  onDelete,
  onSetDefault,
  onManageColumns,
  onUpdateColumnVisibility,
  className = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [newViewName, setNewViewName] = useState("");
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [showColumnModal, setShowColumnModal] = useState(false);
  // The view awaiting a confirmed delete; also what the modal renders.
  const [pendingDeleteView, setPendingDeleteView] = useState(null);

  const containerRef = useRef(null);
  const viewsTriggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setRenamingId(null);
      setRenameValue("");
      setPendingDeleteView(null);

    }
  }, [isOpen]);

  const run = async (fn) => {
    setBusy(true);
    setLocalError(null);
    try {
      await fn();
    } catch (err) {
      const message =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong";
      setLocalError(message);
    } finally {
      setBusy(false);
    }
  };

  const handleSaveNew = () =>
    run(async () => {
      if (!newViewName.trim()) return;
      await onSaveNew?.(newViewName.trim());
      setNewViewName("");
      setShowSaveInput(false);
      setIsOpen(false);
    });

  const startRename = (view, e) => {
    e?.stopPropagation?.();
    setRenamingId(view.id);
    setRenameValue(view.name);
  };

  const cancelRename = () => {
    setRenamingId(null);
    setRenameValue("");
  };

  const commitRename = (viewId) =>
    run(async () => {
      const trimmed = renameValue.trim();
      if (!trimmed) return;
      await onRename?.(viewId, trimmed);
      cancelRename();
    });

  const handleDelete = (view, e) => {
    e?.stopPropagation?.();
    setPendingDeleteView(view);
  };

  const confirmDelete = () =>
    run(async () => {
      const view = pendingDeleteView;
      await onDelete?.(view.id);
      setPendingDeleteView(null);
      if (activeView?.id === view.id) {
        setIsOpen(false);
      }
    });

  const handleSetDefault = (viewId, e) => {
    e?.stopPropagation?.();
    run(async () => {
      await onSetDefault?.(viewId);
    });

  };

  const canRename = typeof onRename === "function";
  const canDelete = typeof onDelete === "function";
  const canSetDefault = typeof onSetDefault === "function";
  const hasDefaultView = views.some((v) => v.isDefault);


  const displayError = localError || error;

  return (
    <>
      <div ref={containerRef} data-table-saved-views className={`relative w-fit ${className}`}>
        <button
          ref={viewsTriggerRef}
          type="button"
          onClick={() => setIsOpen((p) => !p)}
          disabled={loading}
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium border transition-all duration-150 disabled:opacity-60 ${
            isDirty
              ? "bg-[#223688] border-[#223688] text-white hover:bg-[#1a2b6d]"
              : "bg-white dark:bg-acs-dark-surface border-gray-300 dark:border-acs-dark-border text-gray-700 dark:text-acs-dark-text hover:border-[#223688] dark:hover:border-[#223688] hover:text-[#223688] dark:hover:text-[#223688]"
          }`}
        >
          {loading ? (
            <Loader2 size={15} className="animate-spin shrink-0" />
          ) : (
            <Columns3 size={15} className="shrink-0" />
          )}
          <span>
            {label}: {controlLabel}
            {isDirty ? " *" : ""}
          </span>
          <ChevronDown
            size={15}
            className={`shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
          />
        </button>

        {isOpen && (
          <div className="absolute right-0 z-[200] mt-2 min-w-[280px] rounded-md border border-gray-200 bg-white shadow-lg dark:border-acs-dark-border dark:bg-acs-dark-surface">
            {displayError && (
              <p className="border-b border-red-100 bg-red-50 px-3 py-2 text-xs text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
                {displayError}
              </p>
            )}

            <div className="max-h-[240px] overflow-y-auto py-1">
              {views.length === 0 && !loading && (
                <p className="px-4 py-2 text-xs text-gray-500 dark:text-acs-dark-subtext">
                  No saved views yet.
                </p>
              )}

              {views.map((view) => (
                <div
                  key={view.id}
                  className={`px-2 py-1 hover:bg-gray-50 dark:hover:bg-acs-dark-hover ${
                    activeView?.id === view.id
                      ? "bg-[#223688]/10 dark:bg-[#223688]/30"
                      : ""
                  }`}
                >
                  {renamingId === view.id ? (
                    <div
                      className="flex items-center gap-1 px-1 py-0.5"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <input
                        value={renameValue}
                        onChange={(e) => setRenameValue(e.target.value)}
                        placeholder="View name"
                        autoFocus
                        className="min-w-0 flex-1 rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(view.id);
                          if (e.key === "Escape") cancelRename();
                        }}
                      />
                      <button
                        type="button"
                        title="Save name"
                        disabled={busy || !renameValue.trim()}
                        className="rounded p-1 text-[#223688] hover:bg-gray-100 disabled:opacity-40 dark:hover:bg-acs-dark-hover"
                        onClick={() => commitRename(view.id)}
                      >
                        <Check size={14} />
                      </button>
                      <button
                        type="button"
                        title="Cancel edit"
                        className="rounded p-1 text-gray-400 hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                        onClick={cancelRename}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 px-1 py-1.5">
                      <button
                        type="button"
                        title="Use this view"
                        disabled={busy}
                        className={`mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-none ${
                          activeView?.id === view.id
                            ? "border-[#223688] bg-[#223688]"
                            : "border-gray-300 bg-white hover:border-gray-400 dark:border-acs-dark-border dark:bg-acs-dark-surface"
                        }`}
                        onClick={() => {
                          onSelectView?.(view);
                          setIsOpen(false);
                        }}
                      >
                        {activeView?.id === view.id && (
                          <span className="block h-1.5 w-1.5 rounded-full bg-white" />
                        )}
                      </button>
                      {canRename ? (
                        <button
                          type="button"
                          title="Click to rename"
                          disabled={busy}
                          className={`min-w-0 flex-1 truncate text-left text-sm hover:underline ${
                            activeView?.id === view.id ? "font-semibold" : ""
                          }`}
                          onClick={(e) => startRename(view, e)}
                        >
                          {view.name}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy}
                          className={`min-w-0 flex-1 truncate text-left text-sm ${
                            activeView?.id === view.id ? "font-semibold" : ""
                          }`}
                          onClick={() => {
                            onSelectView?.(view);
                            setIsOpen(false);
                          }}
                        >
                          {view.name}
                        </button>
                      )}
                      {canSetDefault && (
                        <button
                          type="button"
                          title={view.isDefault ? "Default view on login" : "Set as default view on login"}
                          disabled={busy || view.isDefault}
                          className={`rounded p-1 ${
                            view.isDefault
                              ? "text-amber-500"
                              : "text-gray-300 hover:text-amber-500 dark:text-acs-dark-subtext dark:hover:text-amber-500"
                          }`}
                          onClick={(e) => handleSetDefault(view.id, e)}
                        >
                          <Star size={14} fill={view.isDefault ? "currentColor" : "none"} />
                        </button>
                      )}

                      {canDelete && (
                        <button
                          type="button"
                          title="Delete view"
                          disabled={busy}
                          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
                          onClick={(e) => handleDelete(view, e)}
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}

              <div
                className={`px-2 py-1 hover:bg-gray-50 dark:hover:bg-acs-dark-hover ${
                  !activeView ? "bg-[#223688]/10 dark:bg-[#223688]/30" : ""
                }`}
              >
                <div className="flex items-center gap-1 px-1 py-1.5">
                  <button
                    type="button"
                    title="Use system default"
                    disabled={busy}
                    className={`mr-2 flex h-5 w-5 shrink-0 items-center justify-center rounded-full border transition-none ${
                      !activeView
                        ? "border-[#223688] bg-[#223688]"
                        : "border-gray-300 bg-white hover:border-gray-400 dark:border-acs-dark-border dark:bg-acs-dark-surface"
                    }`}
                    onClick={() => {
                      onSelectView?.(null);
                      setIsOpen(false);
                    }}
                  >
                    {!activeView && (
                      <span className="block h-1.5 w-1.5 rounded-full bg-white" />
                    )}
                  </button>
                  <button
                    type="button"
                    disabled={busy}
                    className={`min-w-0 flex-1 truncate text-left text-sm ${
                      !activeView ? "font-semibold" : ""
                    }`}
                    onClick={() => {
                      onSelectView?.(null);
                      setIsOpen(false);
                    }}
                  >
                    System default
                  </button>
                  {canSetDefault && (
                    <button
                      type="button"
                      title={!hasDefaultView ? "Default view on login" : "Set as default view on login"}
                      disabled={busy || !hasDefaultView}
                      className={`rounded p-1 ${
                        !hasDefaultView
                          ? "text-amber-500"
                          : "text-gray-300 hover:text-amber-500 dark:text-acs-dark-subtext dark:hover:text-amber-500"
                      }`}
                      onClick={(e) => handleSetDefault(null, e)}
                    >
                      <Star size={14} fill={!hasDefaultView ? "currentColor" : "none"} />
                    </button>
                  )}

                </div>
              </div>
            </div>

            <div className="border-t border-gray-200 p-2 dark:border-acs-dark-border">
              {showSaveInput ? (
                <div className="flex flex-col gap-2">
                  <input
                    value={newViewName}
                    onChange={(e) => setNewViewName(e.target.value)}
                    placeholder="View name"
                    className="w-full rounded border px-2 py-1 text-sm dark:border-acs-dark-border dark:bg-acs-dark-surface"
                    onKeyDown={(e) => e.key === "Enter" && handleSaveNew()}
                  />
                  <div className="flex gap-2">
                    <button
                      type="button"
                      className="flex-1 rounded bg-[#223688] px-2 py-1 text-xs font-semibold text-white"
                      disabled={busy || !newViewName.trim()}
                      onClick={handleSaveNew}
                    >
                      Save
                    </button>
                    <button
                      type="button"
                      className="rounded px-2 py-1 text-xs text-gray-600"
                      onClick={() => setShowSaveInput(false)}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col gap-1">
                  {activeView && (
                    <button
                      type="button"
                      disabled={!isDirty || busy}
                      className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 disabled:opacity-50 dark:hover:bg-acs-dark-hover"
                      onClick={() =>
                        run(async () => {
                          await onUpdateActive?.();
                          setIsOpen(false);
                        })
                      }
                    >
                      <Save size={14} /> Update &quot;{activeView.name}&quot;
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                    onClick={() => setShowSaveInput(true)}
                  >
                    <Save size={14} /> Save as new view
                  </button>
                  {canDelete && activeView && (
                    <button
                      type="button"
                      disabled={busy}
                      className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/30"
                      onClick={(e) => handleDelete(activeView, e)}
                    >
                      <Trash2 size={14} /> Delete &quot;{activeView.name}&quot;
                    </button>
                  )}
                  <button
                    type="button"
                    className="flex items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-gray-100 dark:hover:bg-acs-dark-hover"
                    onClick={() => {
                      setIsOpen(false);
                      if (onManageColumns) {
                        onManageColumns();
                      } else {
                        setShowColumnModal(true);
                      }
                    }}
                  >
                    <Columns3 size={14} /> Manage columns
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        <ConfirmDeleteModal
          isOpen={pendingDeleteView != null}
          title="Delete view"
          headline={`Delete the \u201c${pendingDeleteView?.name ?? ""}\u201d view?`}
          description="This cannot be undone. The table keeps its current layout — only the saved view is deleted."
          confirmLabel="Delete view"
          busy={busy}
          error={localError}
          onCancel={() => setPendingDeleteView(null)}
          onConfirm={confirmDelete}
        />

      </div>

      <TableColumnManagerModal
        isOpen={showColumnModal}
        onClose={() => setShowColumnModal(false)}
        allDataHeaders={allDataHeaders}
        visibleDataHeaders={dataHeaders}
        onApply={(visible) => {
          onUpdateColumnVisibility?.(visible);
          setShowColumnModal(false);
        }}
      />
    </>
  );
};

export default TableSavedViewsControl;
